import { getSearchWithoutActionParams } from '@/lib/plp-panel-url-state';

export const LOGIN_REGISTER_VIEW_PARAM = 'view';
export const LOGIN_REGISTER_VIEW_VALUE = 'register';

const LOGIN_RETURN_LOOP_SUFFIXES = ['/login', '/complete-profile'] as const;

function parseSearchParams(search: string): URLSearchParams {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

function toSearchString(params: URLSearchParams): string {
    const value = params.toString();
    return value ? `?${value}` : '';
}

/** True when the login URL marks the inline registration profile step. */
export function isLoginRegisterView(search: string): boolean {
    return parseSearchParams(search).get(LOGIN_REGISTER_VIEW_PARAM) === LOGIN_REGISTER_VIEW_VALUE;
}

/** Adds `view=register` while preserving other query params (email stays out of the URL). */
export function appendLoginRegisterViewSearch(search: string): string {
    const params = parseSearchParams(search);
    params.set(LOGIN_REGISTER_VIEW_PARAM, LOGIN_REGISTER_VIEW_VALUE);
    return toSearchString(params);
}

/** Removes `view=register` while preserving other query params. */
export function stripLoginRegisterViewSearch(search: string): string {
    const params = parseSearchParams(search);
    params.delete(LOGIN_REGISTER_VIEW_PARAM);
    return toSearchString(params);
}

export type LoginActionResponse = {
    success: boolean;
    error?: string;
    redirectUrl?: string;
    showOTPForm?: boolean;
    /** No SLAS account for this email — start the inline registration flow */
    startRegistration?: boolean;
    email?: string;
    newAccount?: boolean;
    unavailable?: boolean;
};

/** Router state passed when redirecting from LoginDrawer to /login for registration */
export type LoginRegistrationNavigationState = {
    startRegistration: true;
    email: string;
};

export function getAuthenticationTarget(destination?: string | null): 'shop' | 'club' {
    return destination?.toLowerCase().includes('club') ? 'club' : 'shop';
}

export function buildLoginActionSearchParams(returnUrl?: string, action?: string, actionParams?: string): string {
    const params = new URLSearchParams();
    if (returnUrl) {
        params.set('returnUrl', returnUrl);
    }
    if (action) {
        params.set('action', action);
    }
    if (actionParams) {
        params.set('actionParams', actionParams);
    }
    return params.toString();
}

export function buildLoginActionUrl(
    loginPath: string,
    returnUrl?: string,
    action?: string,
    actionParams?: string
): string {
    const search = buildLoginActionSearchParams(returnUrl, action, actionParams);
    if (!search) {
        return loginPath;
    }
    return loginPath.includes('?') ? `${loginPath}&${search}` : `${loginPath}?${search}`;
}

/** Current path plus search, without pending-action query params. */
export function getCleanReturnPath(pathname: string, search = ''): string {
    return `${pathname}${getSearchWithoutActionParams(search)}`;
}

function isLoginReturnLoopPath(pathname: string): boolean {
    return LOGIN_RETURN_LOOP_SUFFIXES.some((suffix) => pathname.endsWith(suffix));
}

/** Login href that carries the current page as `returnUrl`, unless that would loop. */
export function buildLoginHrefFromLocation(pathname: string, search = ''): string {
    if (isLoginReturnLoopPath(pathname)) {
        return '/login';
    }
    return buildLoginActionUrl('/login', getCleanReturnPath(pathname, search));
}

/**
 * Login URL for the registration branch: pending action is embedded only in returnUrl
 * so app-wide pending-action hooks do not fire on /login before registration completes.
 */
export function buildLoginRegistrationUrl(
    loginPath: string,
    returnUrl?: string,
    action?: string,
    actionParams?: string
): string {
    const embeddedReturnUrl = buildRedirectPathWithPendingAction(returnUrl, action, actionParams) ?? returnUrl;
    if (!embeddedReturnUrl) {
        return loginPath;
    }
    const search = new URLSearchParams({ returnUrl: embeddedReturnUrl }).toString();
    return loginPath.includes('?') ? `${loginPath}&${search}` : `${loginPath}?${search}`;
}

export function buildRedirectPathWithPendingAction(
    returnUrl: string | undefined,
    action?: string,
    actionParams?: string
): string | undefined {
    if (!returnUrl) {
        return undefined;
    }

    if (!action && !actionParams) {
        return returnUrl;
    }

    const url = new URL(returnUrl, 'http://localhost');
    if (action) {
        url.searchParams.set('action', action);
    }
    if (actionParams) {
        url.searchParams.set('actionParams', actionParams);
    }
    return url.pathname + url.search;
}

/** Whether returnUrl already carries a pending action (e.g. from buildLoginRegistrationUrl). */
export function returnUrlHasEmbeddedPendingAction(returnUrl: string | undefined): boolean {
    if (!returnUrl) {
        return false;
    }
    try {
        return Boolean(new URL(returnUrl, 'http://localhost').searchParams.get('action'));
    } catch {
        return false;
    }
}

/**
 * Resolves where to send the shopper after registration when pending actions may be
 * embedded in returnUrl (registration flow) or passed as separate loader fields.
 */
export function resolvePostRegistrationDestination(
    returnUrl: string | undefined,
    action?: string | null,
    actionParams?: string | null
): string | undefined {
    if (!returnUrl) {
        return undefined;
    }
    if (action) {
        return buildRedirectPathWithPendingAction(returnUrl, action, actionParams ?? undefined);
    }
    if (returnUrlHasEmbeddedPendingAction(returnUrl)) {
        return returnUrl;
    }
    return undefined;
}
